import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { RaceDaysDayItem, RaceDaysTableService } from '../service/raceDaysTable.service';
import { SeasonCalendarEntry, SeasonCalendarService } from '../service/seasonCalendar.service';

/** Plan of a season whose calendar is not finished yet: the rounds that have no
 *  `race_days` row so far are still shown, derived from these numbers. */
export interface RaceDaysPlanPreview {
    plannedRaces: number | null;
    startDate: string | null;
    cadenceDays: number | null;
    /** Small line below the table title, e.g. "Vorläufige Planung". */
    note?: string | null;
}

/** One line of the calendar. Race days, still-open rounds, qualification
 *  sessions and season events are normalized into this single shape so the
 *  table does not have to branch per source. */
export interface CalendarRow {
    entry_type: 'raceday' | 'open' | 'qualification' | 'event';
    /** Badge in the first column — "R3"/"Q1", or an icon when there is no number. */
    badge: string | null;
    badge_icon: string | null;
    title: string;
    day: string | null;                 // "YYYY-MM-DD"
    /** Race run / date passed — drives the greyed-out styling. */
    done: boolean;
    /** Round without a circuit yet — shown as "Strecke offen". */
    circuit_open: boolean;
    circuit_icon_small: string | null;  // flag, when the entry has a circuit
    type_icon: string | null;           // stands in for a missing flag
    info1: string | null;
    info2: string | null;
    group_label: string | null;
}

const OPEN_ROUND_LABEL = 'Strecke offen';
const QUALIFICATION_LABEL = 'Qualifikation';

/** Same-day order: a race day outranks a shootout, which outranks an event —
 *  the precedence the start-page countdown uses (nextRaceDay.php). */
const TYPE_ORDER: Record<CalendarRow['entry_type'], number> = {
    raceday: 0,
    open: 0,
    qualification: 1,
    event: 2
};

@Component({
    selector: 'app-raceDaysOverview-widget',
    standalone: true,
    templateUrl: './raceDaysOverview.component.html',
    styleUrls: ['./raceDaysOverview.component.scss'],
    imports: [
        CommonModule,
        TableModule,
    ],
    providers: [
        RaceDaysTableService,
        SeasonCalendarService,
    ]
})
export class RaceDaysOverviewWidgetComponent implements OnInit {

    public raceDaysTable: CalendarRow[] = [];
    public raceDaysTableLoaded: boolean = false;
    @Input() season_id: number | null = null; // Accept season as an input
    @Input() seasonName: string | null = null; // Season display name; falls back to "Season {id}"
    @Input() group_id: number | null = null; // Accept group as an input
    @Input() color_season: boolean = false; // Accept color_season as an input
    @Input() plan: RaceDaysPlanPreview | null = null; // Fills up not-yet-scheduled rounds

    // All race days and calendar entries, fetched once; input changes only refilter.
    private allDays: RaceDaysDayItem[] | null = null;
    private allEntries: SeasonCalendarEntry[] = [];

    constructor(
        private raceDaysTableService: RaceDaysTableService,
        private seasonCalendarService: SeasonCalendarService
    ) {}

    ngOnInit() {
        try {
            forkJoin({
                days: this.raceDaysTableService.getRaceDaysTable(),
                entries: this.seasonCalendarService.getEntries()
            }).subscribe(({ days, entries }) => {
                this.allDays = days;
                this.allEntries = entries;
                this.applyFilter();
                this.raceDaysTableLoaded = true;
            });
        } catch (error) {
            console.error('Error fetching race days table:', error);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const changed = ['season_id', 'group_id', 'plan'].some(
            (input) => changes[input] && !changes[input].firstChange
        );
        if (changed) {
            this.applyFilter();
        }
    }

    private applyFilter() {
        let days = this.allDays ?? [];
        // Filter by season if the input is provided
        days = this.season_id
            ? days.filter((day) => day.season_id === this.season_id)
            : [...days];

        days = this.group_id
            ? days.filter((day) => day.group_id === this.group_id)
            : days;

        // Sort the filtered results by day_planned
        days.sort(
            (a, b) =>
                new Date(a.day_planned ?? 0).getTime() -
                new Date(b.day_planned ?? 0).getTime()
        );

        // Without a group filter each group contributes a row per round;
        // collapse those into one calendar entry each.
        const rounds = this.group_id ? days : this.collapseRounds(days);
        const raceRows = this.fillOpenRounds(rounds).map((round) => this.toRaceRow(round));

        // Shootouts and season events belong to the whole season, so a group
        // filter must not drop them.
        const entryRows = this.allEntries
            .filter((entry) => !this.season_id || entry.season_id === this.season_id)
            .map((entry) => this.toEntryRow(entry));

        this.raceDaysTable = this.numberRounds(this.sortByDay([...raceRows, ...entryRows]));
    }

    private toRaceRow(round: any): CalendarRow {
        // A scheduled day whose circuit is unknown reads like a still-open round.
        const circuitOpen = !round.circuit_name;
        return {
            entry_type: round.open_round ? 'open' : 'raceday',
            badge: null,
            badge_icon: null,
            title: circuitOpen ? OPEN_ROUND_LABEL : round.circuit_name,
            day: round.day_planned ?? null,
            done: !!round.day_real,
            circuit_open: circuitOpen,
            circuit_icon_small: circuitOpen ? null : round.circuit_icon_small ?? null,
            type_icon: circuitOpen ? 'pi pi-question-circle' : null,
            info1: round.add_info1 ?? null,
            info2: round.add_info2 ?? null,
            group_label: round.group_label ?? null
        };
    }

    private toEntryRow(entry: SeasonCalendarEntry): CalendarRow {
        const isQualification = entry.entry_type === 'qualification';
        const title = (entry.title ?? '').trim();
        return {
            entry_type: entry.entry_type,
            badge: null,
            // Events have no round number; the shootouts get one in numberRounds().
            badge_icon: isQualification ? null : 'pi pi-star-fill',
            title: title || (isQualification ? QUALIFICATION_LABEL : ''),
            day: entry.entry_date ?? null,
            // No day_real for these — they are over once their day has passed.
            done: this.isPast(entry.entry_date ?? null),
            circuit_open: false,
            circuit_icon_small: entry.circuit_icon_small ?? null,
            type_icon: entry.circuit_icon_small
                ? null
                : isQualification
                  ? 'pi pi-stopwatch'
                  : 'pi pi-calendar',
            info1: entry.circuit_name ?? null,
            info2: null,
            group_label: null
        };
    }

    /** Chronological; entries without a date go last. On the same day the more
     *  important type wins. */
    private sortByDay(rows: CalendarRow[]): CalendarRow[] {
        return rows.sort(
            (a, b) =>
                (a.day ?? '9999-99-99').localeCompare(b.day ?? '9999-99-99') ||
                TYPE_ORDER[a.entry_type] - TYPE_ORDER[b.entry_type]
        );
    }

    /** Race rounds are numbered R1..Rn across the season — shootouts and events
     *  sit between them without consuming a round number. */
    private numberRounds(rows: CalendarRow[]): CalendarRow[] {
        const qualificationCount = rows.filter((row) => row.entry_type === 'qualification').length;
        let round = 0;
        let qualification = 0;
        rows.forEach((row) => {
            if (row.entry_type === 'raceday' || row.entry_type === 'open') {
                row.badge = `R${++round}`;
            } else if (row.entry_type === 'qualification') {
                qualification++;
                row.badge = qualificationCount > 1 ? `Q${qualification}` : 'Q';
            }
        });
        return rows;
    }

    private isPast(day: string | null): boolean {
        const date = this.parseDay(day);
        if (!date) {
            return false;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    }

    /** A season keeps race days only for the rounds whose circuit is decided —
     *  a round without one is not stored at all (`race_days.circuit_id` is NOT
     *  NULL). Those rounds are shown as open slots, on the race day they are
     *  planned for: the season's date grid (start + n × cadence) minus the days
     *  that are already scheduled. That keeps an undecided round in its own slot
     *  instead of pushing it to the end of the season. */
    private fillOpenRounds(rounds: any[]): any[] {
        const missing = (this.plan?.plannedRaces ?? 0) - rounds.length;
        if (missing <= 0) {
            return rounds;
        }
        const scheduled = new Set(rounds.map((round) => round.day_planned));
        const open = this.plannedDays()
            .filter((day) => !scheduled.has(day))
            .slice(0, missing);
        // Without a date grid the rounds are known but their days are not.
        while (open.length < missing) {
            open.push(null);
        }
        return [
            ...rounds,
            // No circuit_name — an open round renders like any other round whose
            // circuit is not decided yet.
            ...open.map((day) => ({
                season_id: this.season_id,
                day_planned: day,
                day_real: null,
                open_round: true
            }))
        ];
    }

    /** Every race day the season plans for: `start_date` + n × cadence. Empty
     *  when the plan has no date to anchor on — the first scheduled day stands
     *  in for a missing start date. */
    private plannedDays(): (string | null)[] {
        const cadence = this.plan?.cadenceDays && this.plan.cadenceDays > 0 ? this.plan.cadenceDays : null;
        const anchor = this.parseDay(this.plan?.startDate ?? null) ?? this.firstScheduledDay();
        if (!cadence || !anchor) {
            return [];
        }
        return Array.from({ length: this.plan?.plannedRaces ?? 0 }, (_, index) =>
            this.toDayString(this.addDays(anchor, index * cadence) ?? anchor)
        );
    }

    private firstScheduledDay(): Date | null {
        const days = (this.allDays ?? [])
            .filter((day) => !this.season_id || day.season_id === this.season_id)
            .map((day) => day.day_planned)
            .filter((day): day is string => !!day)
            .sort();
        return this.parseDay(days[0] ?? null);
    }

    /** `YYYY-MM-DD` → local date; `new Date(str)` would parse it as UTC midnight
     *  and shift the day for viewers west of UTC. */
    private parseDay(day: string | null | undefined): Date | null {
        const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(day ?? '');
        return parts ? new Date(+parts[1], +parts[2] - 1, +parts[3]) : null;
    }

    private addDays(day: Date | null, days: number | null): Date | null {
        if (!day || !days) {
            return null;
        }
        const next = new Date(day);
        next.setDate(next.getDate() + days);
        return next;
    }

    private toDayString(day: Date): string {
        const pad = (value: number) => `${value}`.padStart(2, '0');
        return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
    }

    /** Groups usually race the same circuit on the same date — show that round
     *  once. Early seasons alternated weekends per group instead; only in such
     *  seasons (groups never share a date) the rows carry the group's name,
     *  since a round missing some group there is a real per-group calendar
     *  entry, not just an incomplete schedule. */
    private collapseRounds(days: RaceDaysDayItem[]): any[] {
        const groupCount = new Set(days.map((d) => d.group_id)).size;
        const rounds = new Map<string, RaceDaysDayItem[]>();
        days.forEach((day) => {
            const key = `${day.day_planned}|${day.circuit_id}`;
            rounds.set(key, [...(rounds.get(key) ?? []), day]);
        });
        const perGroupCalendar = groupCount > 1 && [...rounds.values()].every((rows) => rows.length === 1);
        return [...rounds.values()].map((rows) => ({
            ...rows[0],
            add_info1: rows.map((r) => r.add_info1).find((v) => v) ?? null,
            add_info2: rows.map((r) => r.add_info2).find((v) => v) ?? null,
            // A round only counts as raced once every group has run it.
            day_real: rows.every((r) => r.day_real) ? rows[0].day_real : null,
            group_label: perGroupCalendar ? rows[0].group_name : null
        }));
    }

    getRowClass(row: CalendarRow): string {
        if ( this.color_season ) {
            if ( !row.done ) {
                return 'highlight-togo-row';
            }else{
             return 'highlight-done-row';
         }
        }
        else {
            return '';
        }
    }
}
